import {
	FilledOutFormStatus,
	FieldTeam,
	FieldWorker,
	JobQuoteStatus,
	DwrFormTypeOfDay,
	DwrFormNonBillableDayReason,
	JobStatuses,
	PayrollBatchPayrollType,
	PayrollBatchStatus,
	PayrollBatchGroupStatus,
	RailroadOvertimeBasis,
	RailroadRegularTimeBasis,
	JobNetworkOrServiceOrder,
	FlashAuditSummaryStatus,
	InvoiceStatuses,
	DayClassifications,
	ResolutionTypes,
	JobTrackID,
	JobEndMarket,
	JobType,
	RailroadClass,
	FieldWorkerLanguage,
} from './../api/api';
import { QuoteLogAction } from 'src/shared/api';

export function displayJobStatusAdmin(value: string): string {
	switch (value) {
		case 'CustomerEntry':
			return 'Not Submitted';
		case 'Submitted':
			return 'Needs Reviewing';
		case 'Reviewing':
			return 'Requested Approval';
		case 'Approved':
			return 'Approved';
		case 'InProgress':
			return 'Job In Progress';
		case 'Completed':
			return 'Job Completed';
		case 'Cancelled':
			return 'Cancelled';
		default:
			return value;
	}
}

export function displayFilledOutFormStatus(status: FilledOutFormStatus) {
	return status.description;
}

export function displayStatus(value: any): any {
	return value;
}

export function displayItem(item?: any): string {
	if (item) {
		if (item.companyName) {
			return item.companyName;
		} else if (item.name) {
			return item.name;
		} else {
			return null;
		}
	} else {
		return null;
	}
}

export function displayRwicName(rwic: FieldWorker) {
	if (rwic) {
		if (rwic.firstName && rwic.lastName) {
			return `${rwic.firstName}, ${rwic.lastName}`;
		} else {
			return null;
		}
	} else {
		return null;
	}
}

export function displayNumber(item?: { number: string }): string | undefined {
	return item ? item.number : undefined;
}

export function displayName(item?: { name: string }): string | undefined {
	return item ? item.name : undefined;
}

export function displayCompanyName(item?: any): string {
	if (item && item.name) {
		return item.name;
	}
	return item;
}

export const displayQuoteLogAction: {
	[K in keyof typeof QuoteLogAction]: string;
} = {
	Cancellation: 'Cancelled Quote',
	CommentToCustomer: 'Commented',
	CustomerApproval: 'Approved the Quote',
	CustomerComment: 'Commented',
	CustomerSubmission: 'Submitted a New Quote Request',
	EstimateToCustomer: 'Submitted a Quote Estimate',
	RailProsApproval: 'Quote Approved',
	Creation: 'Creation',
	Revision: 'Revision',
};

export const noEmailAddress = (emailAddress: string) => {
	return emailAddress && emailAddress.substring(0, emailAddress.indexOf('@'));
};

export const displayJobStatus: {
	[K in keyof typeof JobStatuses]: string;
} = {
	CustomerEntry: 'Saved',
	Submitted: 'Quote Requested',
	Reviewing: 'Action Required',
	Approved: 'Approved',
	InProgress: 'Job In Progress',
	Completed: 'Job Completed',
	Cancelled: 'Cancelled',
};

export const displayResolutionType: {
	[K in keyof typeof ResolutionTypes]: string;
} = {
	InsertAutoApproved: 'Insert Auto Approved',
	UpdateAutoApproved: 'Update Auto Approved',
	UpdateManualPending: 'Update Manual Pending',
	Failed: 'Failed',
	Approved: 'Approved',
	Rejected: 'Rejected',
};

export const displayTypeOfDay: {
	[K in keyof typeof DwrFormTypeOfDay]: string;
} = {
	BillableDay: 'Billable Day',
	ConferenceCall: 'Conference Call',
	DrugTest: 'Drug Test',
	FieldTraining: 'New Hire Training',
	JobSetup: 'Job Setup',
	RulesClass: 'Rules Class',
	TerritoryQualification: 'Territory Qualification',
	Travel: 'Travel',
	Other: 'Other',
};

export const displayNonBillableDayReason: {
	[key in keyof typeof DwrFormNonBillableDayReason]: string;
} = {
	NoProtection: 'No Protection',
	OpportunityLoss: 'Opportunity Loss',
	HoldOver: 'HoldOver',
	Shadowing: 'Shadowing',
	Equipment: 'Equipment',
	PaidTimeOff: 'PTO',
	Holiday: 'Holiday',
	JuryDuty: 'Jury Duty',
	Bereavement: 'Bereavement',
	Sick: 'Sick',
	TravelMobilization: 'Travel & Mobilization',
	FloatingHoliday: 'Floating Holiday',
};

export const displayQuoteStatus: {
	[K in keyof typeof JobQuoteStatus]: string;
} = {
	NeedsApproval: 'Needs Approval',
	NeedsPaperwork: 'Needs Paperwork',
	NeedsProtection: 'Needs Protection',
	SentToDTM1: 'Sent To DTM 1',
	SentToDTM2: 'Sent To DTM 2',
	SentToDTM3: 'Sent To DTM 3',
	SentToMIPP1: 'Sent To MIPP 1',
	SentToMIPP2: 'Sent To MIPP 2',
	SentToMIPP3: 'Sent To MIPP 3',
	NoResponse: 'No Response',
};

export const displayPayrollType: {
	[K in keyof typeof PayrollBatchPayrollType]: string;
} = {
	RpfsW2: 'RPFS',
	RwtW2: 'RWT',
	RpW2: 'RP',
	As1099: '1099',
	Exempt: 'Exempt',
	Subcontractor: 'Subcontractor',
};

export const displayLanguage: {
	[K in keyof typeof FieldWorkerLanguage]: string;
} = {
	English: 'English',
	Spanish: 'Spanish'
}

export const displayBatchStatus: {
	[K in keyof typeof PayrollBatchStatus]: string;
} = {
	Preparing: 'Not Submitted',
	NotStarted: 'Not Started',
	Error: 'Error Generating Batch',
	Submitted: 'Submitted',
	Canceled: 'Canceled',
	Submitting: 'Submitting',
	Generating: 'Generating',
};

export const displayBatchGroupStatus: {
	[K in keyof typeof PayrollBatchGroupStatus]: string;
} = {
	Preparing: 'Not Submitted',
	NotStarted: 'Not Started',
	PartiallySubmitted: 'Partially Submitted',
	FullySubmitted: 'Fully Submitted',
};

export const displayOvertimeBasis: {
	[K in keyof typeof RailroadOvertimeBasis]: string;
} = {
	Daily: 'Daily',
	Weekly: 'Weekly',
};

export const displayRegularTimeBasis: {
	[K in keyof typeof RailroadRegularTimeBasis]: string;
} = {
	Daily: 'Daily',
	Hourly: 'Hourly',
	Monthly: 'Monthly',
};

export const displayNetworkOrServiceOrder: {
	[K in keyof typeof JobNetworkOrServiceOrder]: string;
} = {
	NotApplicable: 'None',
	Network: 'Network',
	ServiceOrder: 'Service Order',
};

export const displayFlashAuditSummaryStatus: {
	[K in keyof typeof FlashAuditSummaryStatus]: string;
} = {
	Submitted: 'Submitted',
	Pass: 'Pass',
	PassWithException: 'Pass With Exception',
	Fail: 'Fail',
	Expired: 'Expired',
};

export const displayInvoiceStatus: {
	[K in keyof typeof InvoiceStatuses]: string;
} = {
	NotStarted: 'Not Started',
	Preparing: 'Preparing',
	Submitted: 'Submitted',
	Canceled: 'Canceled',
	Submitting: 'Submitting',
	Generating: 'Generating',
	Error: 'Error',
};

export const displayDayClassification: {
	[K in keyof typeof DayClassifications]: string;
} = {
	BillableDay: 'Billable Day',
	ConferenceCall: 'Conference Call',
	DrugTest: 'Drug Test',
	FieldTraining: 'New Hire Training',
	JobSetup: 'Job Setup',
	RulesClass: 'Rules Class',
	TerritoryQualification: 'Territory Qualification',
	Travel: 'Travel',
	Other: 'Other',
};

export const displayDescription = (item: {
	description: string;
}): string | undefined => (item ? item.description : undefined);

export const displayFieldTeam = (fieldTeam: FieldTeam) => {
	return !fieldTeam || fieldTeam.name;
};

export function displayPaycheckStatus(status: string): string {
	switch (status) {
		case 'Preparing':
			return 'Not Submitted';
		default:
			return status;
	}
}

export function displayCompanyForPayroll(status: string): string {
	switch (status) {
		case 'RpfsW2':
			return 'RPFS';
		case 'RwtW2':
			return 'RWT';
		case 'RpW2':
			return 'RP';
		case 'As1099':
			return '1099';
		default:
			return status;
	}
}

export function displayPrevailingWageApp(text: string): string {
	switch (text) {
		case 'NotApplicable':
			return '';
		case 'IsPrevailingWage':
			return 'Yes';
		case 'IsNotPrevailingWage':
			return 'No';
		case 'RegularWageIsHigher':
			return 'Regular Wage Is Higher';
		default:
			return status;
	}
}

export function displayRateType(type: string): string {
	switch (type) {
		case 'NotApplicable':
			return '';
		case 'PrevailingWage1':
			return 'PW1';
		case 'PrevailingWage2':
			return 'PW2';
		case 'PrevailingWage3':
			return 'PW3';
		case 'OverTime':
			return 'OT';
		case 'DoubleTime':
			return 'DT';
		case 'Regular':
			return 'Reg';
		default:
			return status;
	}
}

export function displayEndMarket(type: string): string {
	switch (type) {
		case JobEndMarket.FiberAndCable:
			return 'Fiber & Cable';
		case JobEndMarket.OilAndGas:
			return 'Oil & Gas';
		case JobEndMarket.WaterAndWastewater:
			return 'Water & Wastewater';
		case JobEndMarket.VegetationMgmt:
			return 'Vegetation Management';
		case JobEndMarket.RoadAndBridge:
			return 'Road & Bridge';
		default:
			return type;
	}
}

export function displayJobType(type: string): string {
	switch (type) {
		case JobType.MaintenanceOrUpgrade:
			return 'Maintenance/Upgrade';
		default:
			return type;
	}
}

export function displayRailroadClass(type: string): string {
	switch (type) {
		case RailroadClass.RapidTransit:
			return 'Rapid Transit';
		case RailroadClass.RegionalCommuterCarrier:
			return 'Regional Commuter Carrier';
		default:
			return type;
	}
}

export function compareWithNameFn(o1: any, o2: any) {
	if (o1 && o2) {
		if (o1.name === o2.name) {
			return true;
		}
	}
	return false;
}

export function compareTeam(o1: any, o2: any) {
	if (o1 && o2) {
		if (o1.id === o2.id) {
			return true;
		}
	}
	return false;
}

// The values coming in can vary between string and boolean.
export function trueOrFalseCompare(o1: any, o2: any) {
	return (o1 === 'true') === o2 || o1 === (o2 === 'true') || o1 === o2;
}

export enum Roles {
	Admin = 'Admin',
	FieldDirector = 'FieldDirector',
	FieldManager = 'FieldManager',
	FieldSupport = 'FieldSupport',
	Payroll = 'Payroll',
	Billing = 'Billing',
	HR = 'HR',
}

export enum FieldWorkerType {
	RwicTypeId = 0,
	SupervisorTypeId = 1,
	ManagerTypeId = 2,
	DirectorTypeId = 3,
	SupportTypeId = 4,
	ObserverTypeId = 5,
}

export enum DwrClientFieldType {
	ClientName = 'ClientName',
	ClientPhoneNumber = 'ClientPhoneNumber',
	ClientEmail = 'ClientEmail',
	CompanyName = 'CompanyName',
}

export const displayJobTrackID: {
	[K in keyof typeof JobTrackID]: string;
} = {
	Main: 'Main',
	OtherThanMain: 'Other Than Main',
	MainSiding: 'Main & Siding',
};

export enum FilledOutFormType {
	Dwr = 'Dwr',
	JobSetup = 'JobSetup',
}
